import { mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Drive the car — the key says which way each colour goes, and the key changes.
 *
 * A row of coloured arrows across the top is the key: red goes right, yellow goes up. A car arrives
 * in one of those colours and the child presses the direction the key gives it. Then, partway
 * through the round, the key changes in plain sight — and red means up.
 *
 * **This is the opposite of `ef-rule-switch`, and the pair is the point.** There the rule is hidden
 * and the whole task is inferring from feedback that it moved; a child who never notices is a child
 * who could not work it out. Here the change is printed at the top of the screen in a colour they
 * can see, so failing to follow it is not a failure of inference at all — it is a HABIT. Six cards
 * of red-goes-right builds a hand that goes right, and the work is stopping it.
 *
 * That distinction is why the two are separate families rather than levels of one, and it is also
 * why this one is honest about what it records: **cards after a key change that were answered by
 * the OLD key**. Not a reaction time — PC-1 excludes those, because timing variance across a fleet
 * of cheap Android phones swamps the signal — a count of actions a child can be shown.
 *
 * **The key is a permutation, never a partial map.** Every colour in play has a direction and every
 * direction has a colour, so a card is never ambiguous and a change always moves at least two
 * colours. A key that changed one entry would let a child carry on unaffected most of the round.
 */
/**
 * How many directions a car can be sent: up, right, down, left, numbered clockwise from up.
 *
 * **Only the count is exported, deliberately.** The obvious move is four named constants, and
 * `maze.ts` already exports `UP`, `RIGHT`, `DOWN` and `LEFT` holding a bitmask of walls — same
 * names, completely different numbers. A second set would compile (the barrel would refuse the
 * clash, which is how this was caught) and would be a trap for whoever imported the wrong one.
 *
 * The encoding here is the one `robot.ts` uses for a facing, which is the closest existing thing:
 * 0 up, 1 right, 2 down, 3 left. Nothing in this file needs a name for them, and the renderer
 * turns an index into an angle.
 */
export const KEY_MAP_DIRECTIONS = 4;
export const KEY_MAP_MIN_DIFFICULTY = 1;
export const KEY_MAP_MAX_DIFFICULTY = 8;
/**
 * Eight levels: more colours, then more changes — never shorter runs.
 *
 * **No run is shorter than three cards, and that number is the fix for the worst thing this game
 * shipped with.** The runs used to go down to two, and at the levels with three colours that made
 * the key unusable: a child met two cars, and before the third colour had come round once the whole
 * key changed under them. Two of the three entries had never been needed, so nothing on the bar had
 * become a rule — and a rule nobody has formed is a rule nobody can be asked to break, which is the
 * entire measurement. It read to a teacher as the game not letting the third car be played, which
 * is exactly what it was doing.
 *
 * So a run is long enough for the colours in play to come round, and the ladder gets harder by
 * adding colours and adding switches rather than by cutting the runs short.
 *
 * **Two colours is ONE level now, and it is six cards.** It used to be two levels — seven cards and
 * then ten — and a child had to pass both twice to be rid of them, so thirty-four cards went by
 * before a third colour existed. A teacher watching it described a child losing interest before the
 * game had shown them anything, and that is the honest reading: the two-colour round is not a level,
 * it is an introduction, and an introduction repeated four times is not an introduction. Six cards
 * is the shortest round that still contains a change (two runs of three), which is exactly as much
 * as the introduction has to say. Everything after it moves on the axis the game is actually about.
 *
 * That first level still exists, for the reason the search game opens on six arrows: the thing being
 * learnt is that the strip at the top is an instruction which can CHANGE, and learning that should
 * not also require holding four mappings.
 *
 * The 4-colour tier grows by one or two cards a level rather than on a new axis, and that is a real
 * limit rather than a choice: above four colours there is no fifth direction to map, and the two
 * levers left — more switches, longer runs — are what the top two levels are. A longer round under
 * the same key density is a genuine step (more cards to keep reading the key across without
 * lapsing), and it is the smallest one on the ladder. It is placed where it costs least.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { colours: 2, cards: 6, switches: 1, minRun: 3 };
        case 2:
            return { colours: 3, cards: 8, switches: 1, minRun: 3 };
        case 3:
            return { colours: 3, cards: 10, switches: 2, minRun: 3 };
        case 4:
            return { colours: 4, cards: 9, switches: 1, minRun: 3 };
        case 5:
            return { colours: 4, cards: 10, switches: 2, minRun: 3 };
        case 6:
            return { colours: 4, cards: 12, switches: 2, minRun: 3 };
        case 7:
            return { colours: 4, cards: 13, switches: 3, minRun: 3 };
        /**
         * The longest runs, not the shortest.
         *
         * A ladder that ended by shortening the runs would be ending on the easiest version of the only
         * thing this game measures. The work here is breaking a habit, so the habit has to exist: four
         * cards under one key builds a firmer expectation than three, and firmer is harder to stop.
         */
        default:
            return { colours: 4, cards: 13, switches: 2, minRun: 4 };
    }
}
/**
 * Where the key changes, spaced so every run is at least `minRun` cards long.
 *
 * **Throws when the recipe cannot be honoured, and that is the point of it.** The first version
 * placed what it could and returned early, which is how two levels shipped promising two changes in
 * a round that had room for one: level 2 asked for two changes at three cards apart inside eight
 * cards, and the only legal starts are 3, 4 and 5 — no two of them are three apart. The generator
 * quietly delivered one change, so a level said one thing in its recipe and did another on screen,
 * and nothing anywhere disagreed. A recipe that asks for the impossible is a mistake in the ladder
 * and should read as one.
 */
function switchPointsFor(recipe, rand) {
    const { cards, switches, minRun } = recipe;
    /**
     * The GAPS are drawn, not the positions, and that is what makes this always work.
     *
     * The first version shuffled the legal card indices and took them greedily while they stayed far
     * enough apart. It failed in two different ways. Some recipes had no valid arrangement at all and
     * it returned a short list instead of saying so — level 2 asked for two changes three cards apart
     * inside eight cards, which is impossible, and quietly delivered one. Worse, it also failed on
     * recipes that WERE satisfiable: with runs of three in nine cards the answer is 3 and 6, but a
     * shuffle that offered 4 first left nothing legal after it, so the round came out short depending
     * on the seed.
     *
     * Laying out gaps has neither failure. A round is `switches + 1` runs, each at least `minRun`
     * long; whatever is left over is the slack, and handing pieces of the slack to the runs in order
     * produces a valid arrangement every time. Feasibility is then a single subtraction rather than
     * something discovered by searching — and when it fails, it fails loudly, because a recipe asking
     * for the impossible is a mistake in the ladder and should read as one.
     */
    const slack = cards - minRun * (switches + 1);
    if (slack < 0) {
        throw new Error(`a ${cards}-card round with runs of ${minRun} has no room for ${switches} key changes`);
    }
    const points = [];
    let remaining = slack;
    let at = 0;
    for (let i = 0; i < switches; i++) {
        const extra = Math.floor(rand() * (remaining + 1));
        remaining -= extra;
        at += minRun + extra;
        points.push(at);
    }
    return points;
}
/**
 * Which `colours` directions are in play for a round, drawn from all four rather than always the
 * first `colours` of them.
 *
 * **A fixed "first N" made left unreachable below four colours.** Directions are numbered 0 up, 1
 * right, 2 down, 3 left, and a round used to use exactly `{0, ..., colours - 1}` — so left, sitting
 * last, could only ever appear once a round reached four colours. A child whose difficulty stayed
 * at 1-3 (an entire objective's worth of play) would never once be asked to send a car left; every
 * round was drawn from up/right, or up/right/down. Sampling the set itself means a two-colour round
 * might be told to use right and left, and a child meets every direction from the first round on.
 */
function directionsFor(colours, rand) {
    return shuffle(Array.from({ length: KEY_MAP_DIRECTIONS }, (_, i) => i), rand).slice(0, colours);
}
/**
 * A permutation of `directions`, differing from `previous` everywhere it can.
 *
 * **Every colour moves, and that is what makes a change worth making.** A new key that left one
 * colour where it was would let a child answer a third of the cards after the change out of habit
 * and still be right, so the change would be partly invisible in the data and partly unnecessary
 * for the child. A derangement — no element in its old place — is the version of "the key changed"
 * that means it for every card.
 */
function nextKey(previous, directions, rand) {
    if (!previous)
        return shuffle([...directions], rand);
    for (let attempt = 0; attempt < 40; attempt++) {
        const candidate = shuffle([...directions], rand);
        if (candidate.every((direction, colour) => direction !== previous[colour]))
            return candidate;
    }
    /**
     * The fallback is a rotation through `directions`, which is a derangement by construction.
     *
     * Reached essentially never — at two colours the only permutation that is not the identity is
     * already the swap — but a generator that could return the SAME key on a card the round calls a
     * switch would be recording a change that did not happen, and this loop is the only place that
     * could produce one.
     */
    return previous.map((direction) => {
        const index = directions.indexOf(direction);
        return directions[(index + 1) % directions.length];
    });
}
export function generateKeyMap(task, formId, trialIndex, difficulty) {
    if (difficulty < KEY_MAP_MIN_DIFFICULTY || difficulty > KEY_MAP_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const recipe = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const switchPoints = switchPointsFor(recipe, rand);
    const directions = directionsFor(recipe.colours, rand);
    const keys = [];
    let key = nextKey(null, directions, rand);
    for (let at = 0; at < recipe.cards; at++) {
        if (switchPoints.includes(at))
            key = nextKey(key, directions, rand);
        keys.push([...key]);
    }
    /**
     * The cards, drawn so that the one right after a change is never the same colour as the one
     * before it.
     *
     * The card at a switch point is where the whole measurement happens, and if it repeats the
     * previous colour a child has just been told what that colour does — twice — which is the one
     * arrangement where habit and instruction give the same answer. Every other position is a plain
     * draw with no immediate repeat, because two identical cards in a row read as a stall.
     *
     * **Except at two colours, where "never repeat" was a way to win without playing.** Two colours
     * and no repeats ever leaves exactly one possible deck: strict alternation. So the first level
     * dealt red, yellow, red, yellow, and the answer was alternate the two pads — a child could clear
     * it end to end, key change included, without once looking up at the bar. The level that exists to
     * teach that the bar matters was the one level where it did not, and the round it produced was also
     * the most monotonous thing in the game, which is the same fact wearing two hats.
     *
     * A repeat is therefore allowed exactly where forbidding it would force that cycle — never at a
     * switch point, and never a third time in a row, so neither guarantee above is given up.
     */
    const cards = [];
    for (let at = 0; at < recipe.cards; at++) {
        const previous = cards[at - 1];
        const allowRepeat = recipe.colours === 2 && !switchPoints.includes(at) && cards[at - 2] !== previous;
        let colour = Math.floor(rand() * recipe.colours);
        let guard = 0;
        while (!allowRepeat && previous !== undefined && colour === previous && guard++ < 20) {
            colour = Math.floor(rand() * recipe.colours);
        }
        cards.push(colour);
    }
    return { cards, keys, colours: recipe.colours, switchPoints };
}
/** Which direction a card should be sent, under the key in force for it. */
export function correctDirection(round, at) {
    const key = round.keys[at];
    const colour = round.cards[at];
    if (key === undefined || colour === undefined) {
        throw new Error(`card ${at} is not in this round`);
    }
    return key[colour];
}
/**
 * Cards answered by the key that has just been REPLACED.
 *
 * The measure this game exists for. Counted only where the old key and the new one disagree for
 * that card's colour — which, because a key change is a derangement, is every card — and only
 * after a change rather than throughout, because before the first change there is no old key to
 * fall back on.
 *
 * A plain wrong answer that matches neither key is not counted here. It is still wrong and still
 * scored; it is simply not evidence of the specific thing being described, and folding the two
 * together would let a child who was guessing look like a child who was stuck in a habit.
 */
export function keyMapPerseverations(round, chosen) {
    let count = 0;
    for (let at = 1; at < round.cards.length; at++) {
        const answer = chosen[at];
        if (answer === undefined)
            continue;
        const previousKey = round.keys[at - 1];
        const key = round.keys[at];
        const colour = round.cards[at];
        if (previousKey === undefined || key === undefined || colour === undefined)
            continue;
        // Only where the key actually moved for this colour; unchanged keys have no old answer.
        if (previousKey[colour] === key[colour])
            continue;
        if (answer === previousKey[colour])
            count++;
    }
    return count;
}
/** Cards sent the right way. */
export function scoreKeyMap(round, chosen) {
    let correct = 0;
    for (let at = 0; at < round.cards.length; at++) {
        if (chosen[at] === correctDirection(round, at))
            correct++;
    }
    return correct;
}
/**
 * Whether a round counts as passed.
 *
 * **Not every card.** A round is ten cards with up to four key changes in it, and demanding a clean
 * sweep would make the top levels unpassable for a child who is doing the thing well — one lapse
 * out of ten is what learning to stop a habit looks like. The threshold is all but one, which keeps
 * the ladder honest without turning a single slip into a level drop.
 */
export function passedKeyMap(round, chosen) {
    return scoreKeyMap(round, chosen) >= round.cards.length - 1;
}
//# sourceMappingURL=key-map.js.map